import { appStore, appSelectors } from './app'
import { userStore } from './user'
import { TIER_FEATURES, FEATURE_REQUIREMENTS } from './featureTables'

/**
 * The feature gate, split into a decision and a reading.
 *
 * The decision (`decideFeature`) takes its three inputs as arguments and
 * touches no store. That is not tidiness — it is the only way both frameworks
 * can stay reactive over it. A helper that reads `getState()` internally is
 * invisible to both: a Vue `computed(() => hasFeature(x))` registers no
 * dependency and caches its first answer forever, and a React component that
 * calls it during render never re-renders when the answer changes. Phase 1F
 * shipped exactly that shape for a few commits, and four Vue computeds and two
 * React reads were pinned by it — the same bug Phase 1D fixed for `optimizing`.
 *
 * So: Vue passes the values it read through the Pinia mirror, React passes the
 * values it read through its hooks, and only imperative callers with no render
 * to invalidate — a command, the store's own `requireFeature` — use
 * `hasFeature`, which reads the stores directly.
 */

export type GateInputs = {
  /** `appSelectors.isDemoExperience`: workspace 0 OR a demo tier. Not a state field. */
  isDemo: boolean
  subscriptionTier: string | null | undefined
  /** The server's grants, from /api/auth/features. Empty until they arrive. */
  features: Record<string, { unlocked?: boolean } | undefined> | undefined
}

/** The tier the gate judges by, which is not always the tier on the session. */
export function tierFor(inputs: GateInputs): string {
  // `isDemo` already includes `subscriptionTier === 'demo'` (see
  // `appSelectors.isDemoExperience`), so the Vue original's next branch —
  // "logged in but still marked demo means an evaluation" → 'trial' — could
  // never run, here or there. It is gone rather than kept as a comment that
  // describes behaviour that does not happen.
  if (inputs.isDemo) return 'demo'
  return inputs.subscriptionTier || 'trial'
}

/**
 * Whether a session with these inputs may use a feature.
 *
 * Lifted out of `useFeatureGating()` unchanged — same order, same fallbacks.
 * The server's feature_gates table is authoritative once it has loaded; the
 * tier map is the fallback before that; and a demo experience is judged by the
 * demo map whatever the session holds.
 */
export function decideFeature(featureName: string, inputs: GateInputs): boolean {
  if (!inputs.isDemo) {
    const granted = inputs.features?.[featureName]
    if (granted !== undefined) return Boolean(granted.unlocked)
  }
  const features = TIER_FEATURES[tierFor(inputs)]
  if (!features) return false
  return features[featureName] || false
}

/** The inputs as the stores currently hold them. For imperative callers only. */
export function readGateInputs(): GateInputs {
  const app = appStore.getState()
  return {
    isDemo: appSelectors.isDemoExperience(app),
    subscriptionTier: app.subscriptionTier,
    features: userStore.getState().features,
  }
}

/**
 * The gate, read from the stores right now.
 *
 * Correct for a command or the store's `requireFeature`, which run once in
 * response to a click. NOT correct inside a Vue `computed` or a React render —
 * use `decideFeature` with values you subscribed to. `reactivity.test.ts`
 * sweeps for the mistake.
 */
export function hasFeature(featureName: string): boolean {
  return decideFeature(featureName, readGateInputs())
}

/** The tier the gate judges by, from the stores right now. */
export function currentTier(): string {
  return tierFor(readGateInputs())
}

/** What the upgrade modal should say about a locked feature. */
export function upgradeCopy(featureName: string, displayName?: string | null) {
  const info = FEATURE_REQUIREMENTS[featureName]
  return {
    lockedFeature: displayName || info?.displayName || featureName,
    requiredTier: info?.minTier || 'essential',
  }
}
